/**
 * Compilação "contida" das fórmulas configuráveis do dashboard
 * (colorLinesFormula / formulaIcon do table-card), que são strings
 * de código persistidas em Mktpdashboard.EXPRESSION.
 *
 * Não é um sandbox perfeito (isso não existe no client sem
 * iframe/worker), mas:
 *  - sombreia os globals mais perigosos (window, fetch, eval...);
 *  - roda em strict mode;
 *  - nunca propaga exceção (fórmula quebrada não derruba o grid);
 *  - compila UMA vez por coluna (o código antigo dava eval por célula).
 */

// NOTA: 'eval' e 'arguments' NÃO podem ser nomes de parâmetro em strict
// mode (SyntaxError) — ficam de fora; os vetores de exfiltração/DOM são
// o que importa sombrear.
const SHADOWED_GLOBALS = [
    'window', 'document', 'globalThis', 'self', 'top', 'parent', 'frames',
    'fetch', 'XMLHttpRequest', 'WebSocket', 'EventSource',
    'Function', 'importScripts',
    'localStorage', 'sessionStorage', 'indexedDB', 'navigator', 'location',
];

export type CompiledFormula<TParam, TResult> = (param: TParam) => TResult | undefined;

/**
 * Compila um CORPO de função (`if (...) return {...};`) com um parâmetro
 * nomeado. Retorna null se a fórmula nem compila (sintaxe inválida);
 * a função compilada retorna undefined se lançar em runtime.
 */
export function compileFormula<TParam = unknown, TResult = unknown>(
    body: string,
    paramName = 'params'
): CompiledFormula<TParam, TResult> | null {
    if (!body || typeof body !== 'string') {
        return null;
    }
    let fn: Function;
    try {
        fn = new Function(paramName, ...SHADOWED_GLOBALS, `'use strict';\n${body}`);
    } catch {
        return null;
    }
    return (param: TParam) => {
        try {
            return fn(param) as TResult;
        } catch {
            return undefined;
        }
    };
}

/**
 * Compila uma EXPRESSÃO de função auto-invocada — o formato legado do
 * formulaIcon (`function(){ ... return "classe css"; }` referenciando `data`).
 * `data` entra como parâmetro nomeado (o código antigo injetava o valor
 * por string-replace, vetor de injeção).
 */
export function compileExpressionFormula<TParam = unknown, TResult = unknown>(
    expression: string,
    paramName = 'data'
): CompiledFormula<TParam, TResult> | null {
    if (!expression || typeof expression !== 'string') {
        return null;
    }
    let fn: Function;
    try {
        fn = new Function(paramName, ...SHADOWED_GLOBALS, `'use strict';\nreturn (${expression})();`);
    } catch {
        return null;
    }
    return (param: TParam) => {
        try {
            return fn(param) as TResult;
        } catch {
            return undefined;
        }
    };
}

/**
 * Whitelist para valores interpolados em innerHTML como classe CSS
 * (retorno do formulaIcon). Fecha o vetor de stored-XSS.
 */
export function sanitizeCssClassList(value: unknown): string | null {
    if (typeof value !== 'string' || value.length === 0) {
        return null;
    }
    return /^[\w\s-]+$/.test(value) ? value : null;
}
